import { useEffect, useState } from 'react';
import {
  createPropertyBookingLink,
  deletePropertyBookingLink,
  getMyPropertyBookingLinks,
  updatePropertyBookingLink,
} from 'src/services/client';
import {
  ICalcomApiResult,
  ICreatePropertyBookingLinkRequest,
  IPropertyBookingLink,
  IUpdatePropertyBookingLinkRequest,
  JuniperEventType,
} from 'src/pages/Calendar/CalcomIntegration/types';
import { groupByProperty } from './helpers';

export interface IUseMyPropertyBookingLinks {
  linksFor: (propertyId: string, kind: JuniperEventType) => IPropertyBookingLink[];
  isLoading: boolean;
  error: string | null;

  isSubmitting: boolean;
  createLink: (request: ICreatePropertyBookingLinkRequest) => Promise<boolean>;
  updateLink: (
    bookingLinkId: string,
    request: IUpdatePropertyBookingLinkRequest
  ) => Promise<boolean>;
  removeLink: (bookingLinkId: string) => Promise<boolean>;
}

export const useMyPropertyBookingLinks = (): IUseMyPropertyBookingLinks => {
  const [linksByProperty, setLinksByProperty] = useState<
    Record<string, IPropertyBookingLink[]>
  >({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const fetchLinks = async (): Promise<void> => {
    setError(null);

    const result: ICalcomApiResult<IPropertyBookingLink[]> =
      await getMyPropertyBookingLinks();

    if (!result.ok) {
      setError(result.error ?? 'Failed to fetch booking links');
      return;
    }

    const grouped: Record<string, IPropertyBookingLink[]> = groupByProperty(
      result.data ?? []
    );

    setLinksByProperty(grouped);
  };

  const linksFor = (
    propertyId: string,
    kind: JuniperEventType
  ): IPropertyBookingLink[] => {
    const propertyLinks: IPropertyBookingLink[] =
      linksByProperty[propertyId] ?? [];

    return propertyLinks.filter(
      (link: IPropertyBookingLink) => link.kind === kind
    );
  };

  const createLink = async (
    request: ICreatePropertyBookingLinkRequest
  ): Promise<boolean> => {
    setIsSubmitting(true);
    setError(null);

    const result: ICalcomApiResult<IPropertyBookingLink> =
      await createPropertyBookingLink(request);

    if (!result.ok) {
      setError(result.error ?? 'Failed to create booking link');
      setIsSubmitting(false);
      return false;
    }

    await fetchLinks();
    setIsSubmitting(false);
    return true;
  };

  const updateLink = async (
    bookingLinkId: string,
    request: IUpdatePropertyBookingLinkRequest
  ): Promise<boolean> => {
    setIsSubmitting(true);
    setError(null);

    const result: ICalcomApiResult<IPropertyBookingLink> =
      await updatePropertyBookingLink(bookingLinkId, request);

    if (!result.ok) {
      setError(result.error ?? 'Failed to update booking link');
      setIsSubmitting(false);
      return false;
    }

    await fetchLinks();
    setIsSubmitting(false);
    return true;
  };

  const removeLink = async (bookingLinkId: string): Promise<boolean> => {
    setIsSubmitting(true);
    setError(null);

    const result: ICalcomApiResult<boolean> =
      await deletePropertyBookingLink(bookingLinkId);

    if (!result.ok) {
      setError(result.error ?? 'Failed to remove booking link');
      setIsSubmitting(false);
      return false;
    }

    await fetchLinks();
    setIsSubmitting(false);
    return true;
  };

  useEffect(() => {
    const loadInitial = async (): Promise<void> => {
      setIsLoading(true);
      await fetchLinks();
      setIsLoading(false);
    };

    void loadInitial();
  }, []);

  return {
    linksFor,
    isLoading,
    error,
    isSubmitting,
    createLink,
    updateLink,
    removeLink,
  };
};